import React, { useEffect, useState } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Switch,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import jwtDecode from 'jwt-decode';
import Toastr from './shared/displayComponents/toast/Toastr';
import Players from './players/Players';
import Faq from './faq/Faq';
import AboutUs from './aboutUs/AboutUs';
import SampleProfile from './sampleProfile/SampleProfile';
import NotFound from './shared/displayComponents/notFound/NotFound';
import Footer from './shared/displayComponents/footer/Footer';
import Menu from './shared/displayComponents/menu/Menu';
import CreatePlayer from './players/createPlayer/CreatePlayer';
import PlayerPage from './players/playerPage/playerPage';
import Login from './shared/displayComponents/login/Login';
import FinishProfile from './home/finishProfile';
import ResetPassword from './home/ResetPassword';
import CreateAccount from './shared/displayComponents/createAccount/CreateAccount';
import EditPlayer from './players/editPlayer/EditPlayer';
import RecommendationsPage from './players/recommendedPlayers/RecommendationsPage';
import ComparePage from './players/compare/ComparePage';
import CompareBar from './players/compare/CompareBar';
import AdminDashboard from './admin/AdminDashboard';
import AdminActivity from './admin/AdminActivity';
import CoachInsights from './admin/CoachInsights';
import CoachEngagementList from './admin/CoachEngagementList';
import Home from './home/Home';
import SummerTrips from './marketing/SummerTrips';
import CookieConsent from './shared/displayComponents/cookieConsent/CookieConsent';
import WhatsNew, { CHANGELOG_VERSION } from './shared/displayComponents/whatsNew/WhatsNew';
import routes from './routesDefinitions/routes';
import GuardedRoute from './routesDefinitions/GuardedRoute';
import PlayerGuardedRoute from './routesDefinitions/PlayerGuardedRoute';
import PlayerLoginPage from './messages/player/PlayerLoginPage';
import PlayerInboxPage from './messages/player/PlayerInboxPage';
import PlayerThreadPage from './messages/player/PlayerThreadPage';
import PlayerChecklist from './checklist/PlayerChecklist';
import AdminChecklistTemplate from './checklist/AdminChecklistTemplate';
import AdminChecklistProgress from './admin/AdminChecklistProgress';
import AssistantSetupPage from './admin/AssistantSetupPage';
import AssistantDashboard from './admin/AssistantDashboard';
import AssistantTimeTracking from './timeTracking/AssistantTimeTracking';
import AdminAssistants from './admin/AdminAssistants';
import AdminAssistantProfile from './admin/AdminAssistantProfile';
import AdminOpsDashboard from './admin/AdminOpsDashboard';
import AdminTimeEntries from './admin/AdminTimeEntries';
import AdminDocuments from './admin/AdminDocuments';
import AdminPipeline from './admin/AdminPipeline';
import AdminPageContent from './admin/AdminPageContent';
import AdminPendingCoaches from './admin/AdminPendingCoaches';
import AdminErrorLogs from './admin/AdminErrorLogs';
import PlayerInfoPage from './messages/player/PlayerInfoPage';
import AssistantMyPlayers from './admin/AssistantMyPlayers';
import CoachInboxPage from './messages/coach/CoachInboxPage';
import CoachThreadPage from './messages/coach/CoachThreadPage';
import CoachAdminThreadPage from './messages/coach/CoachAdminThreadPage';
import AdminCoachThreadPage from './messages/admin/AdminCoachThreadPage';
import AdminOversightCoachInboxPage from './messages/admin/AdminOversightCoachInboxPage';
import AdminOversightAssistantInboxPage from './messages/admin/AdminOversightAssistantInboxPage';
import AdminOversightThreadPage from './messages/admin/AdminOversightThreadPage';
import AdminSidebar from './shared/displayComponents/adminSidebar/AdminSidebar';
import CoachBar from './shared/displayComponents/coachBar/CoachBar';
import { extractToken, getUserEmailFromToken } from './shared/utils/tokenUtils';
import {
  setUserEmail,
  setUserRole,
  setIsProfileFinished,
  checkIsProfileFinished,
  getUserRole,
  getUser,
  getPlayerProfile,
} from './shared/store/authSlice';
import { fetchUnreadCount } from './messages/store/conversationsSlice';
import { fetchAdminUnreadCount } from './messages/store/adminConversationsSlice';
import { fetchOversightUnreadCounts } from './messages/store/adminOversightSlice';
import { fetchChecklistUnreadCount } from './checklist/store/checklistSlice';

import './App.scss';


const App = () => {
  const {
    players,
    faq,
    sampleProfile,
    aboutUs,
    home,
    createPlayer,
    login,
    finishProfile,
    createAccount,
    editPlayer,
    recommendations,
    compare,
    adminDashboard,
    adminActivity,
    coachInsights,
    coachEngagement,
    resetPassword,
    favorites,
    summerTrips,
    messages,
    playerLogin,
    playerInbox,
    playerChecklist,
    adminChecklistTemplate,
    adminChecklistProgress,
    assistantSetup,
    assistantDashboard,
    assistantTimeTracking,
    adminAssistants,
    adminOpsDashboard,
    adminTimeEntries,
    assistantProfile,
    assistantMyPlayers,
    assistantPlayerProfile,
    adminDocuments,
    adminPipeline,
    adminPageContent,
    playerInfo,
    adminOversightCoach,
    adminOversightAssistant,
    adminPendingCoaches,
    adminErrorLogs,
  } = routes;
  const token = extractToken({ key: 'authorization' });
  const [isAuthorized, setIsAuthorized] = useState(Boolean(token));
  const { isVisible, message, type } = useSelector(({ shared }) => shared);
  const { isProfileFinished, userEmail, userRole, user } = useSelector(
    ({ authorization }) => authorization,
  );
  const dispatch = useDispatch();
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const showToaster = isVisible ? (
    <Toastr message={message} type={type} />
  ) : null;

  useEffect(() => {
    const decodeTokenAndSetUser = async tokenToDecode => {
      try {
        const decoded = jwtDecode(tokenToDecode);
        dispatch(setUserEmail(decoded.email));
        if (decoded.role === 'player') {
          // Player accounts: set role and profile-finished directly, skip User model calls
          dispatch(setUserRole('player'));
          dispatch(setIsProfileFinished(true));
        }
      } catch (e) {
        const email = await getUserEmailFromToken(tokenToDecode);
        dispatch(setUserEmail(email));
      }
    };
    if (isAuthorized && !userEmail) {
      decodeTokenAndSetUser(token);
    }
  }, [dispatch, isAuthorized, token, userEmail]);

  if (isAuthorized && !window.localStorage.getItem('authorization')) {
    window.localStorage.setItem('authorization', token);
  }

  useEffect(() => {
    // Skip for player accounts — they don't have a User model entry
    if (userEmail && isProfileFinished === null && userRole !== 'player') {
      dispatch(getUser(userEmail));
      dispatch(checkIsProfileFinished({ email: userEmail }));
    }
    // For player accounts, fetch player profile (firstName/lastName)
    if (userEmail && userRole === 'player' && !(user && user.firstName)) {
      dispatch(getPlayerProfile());
    }
  }, [dispatch, isProfileFinished, userEmail, userRole, user]);

  useEffect(() => {
    // Skip for player accounts — role is already set from JWT
    if (userEmail && isProfileFinished && !userRole) {
      dispatch(getUserRole({ email: userEmail }));
    }
  }, [dispatch, isProfileFinished, userEmail, userRole]);

  // Fetch unread counts on mount and whenever the tab regains focus
  useEffect(() => {
    if (!isAuthorized || !userRole) return undefined;
    const refresh = () => {
      dispatch(fetchUnreadCount());
      dispatch(fetchAdminUnreadCount());
      dispatch(fetchOversightUnreadCounts());
      dispatch(fetchChecklistUnreadCount());
    };
    refresh();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [dispatch, isAuthorized, userRole]);

  // Show "What's New" for admin/assistant/coach if unseen in DB
  useEffect(() => {
    if (!isAuthorized || !isProfileFinished || !userRole || userRole === 'player') return;
    if (user && user.universityEmail) {
      setShowWhatsNew(user.lastSeenChangelog !== CHANGELOG_VERSION);
    } else {
      setShowWhatsNew(false);
    }
  }, [isAuthorized, isProfileFinished, user, userRole]);

  // Global cleanup: Remove any leftover modal iframes that block clicks
  useEffect(() => {
    const cleanupIframes = () => {
      // Remove iframes with high z-index (Bootstrap modal issue)
      const iframes = document.querySelectorAll('iframe[style*="z-index"]');
      iframes.forEach(iframe => {
        const style = iframe.getAttribute('style') || '';
        // Check if it's a blocking iframe (high z-index, full screen)
        if (
          style.includes('2147483647') ||
          (style.includes('z-index') &&
           (style.includes('width: 100%') || style.includes('height: 100%')))
        ) {
          // Only remove if it's not a video iframe (YouTube, etc.)
          const src = iframe.getAttribute('src') || '';
          if (!src.includes('youtube') && !src.includes('youtu.be') && !src.includes('vimeo')) {
            iframe.remove();
          }
        }
      });
    };

    // Run cleanup periodically and on visibility change
    const interval = setInterval(cleanupIframes, 1000);
    document.addEventListener('visibilitychange', cleanupIframes);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', cleanupIframes);
    };
  }, []);

  const isAdmin = isAuthorized && isProfileFinished && (userRole === 'admin' || userRole === 'assistant');
  const hasSidebar = isAuthorized && isProfileFinished && Boolean(userRole);

  const routeSwitch = (
    <Switch>
      {/* Public setup routes */}
      <Route exact path={assistantSetup.link}>
        <AssistantSetupPage setIsAuthorized={setIsAuthorized} />
      </Route>
      {/* Player messaging routes */}
      <Route exact path={playerLogin.link}>
        <PlayerLoginPage setIsAuthorized={setIsAuthorized} />
      </Route>
      <PlayerGuardedRoute
        exact
        path="/player/inbox/:conversationId"
        component={PlayerThreadPage}
        auth={isAuthorized}
        userRole={userRole}
      />
      <PlayerGuardedRoute
        exact
        path={playerInbox.link}
        component={PlayerInboxPage}
        auth={isAuthorized}
        userRole={userRole}
      />
      <PlayerGuardedRoute
        exact
        path={playerChecklist.link}
        component={PlayerChecklist}
        auth={isAuthorized}
        userRole={userRole}
      />
      <GuardedRoute
        exact
        path={playerInfo.link}
        component={PlayerInfoPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['player', 'admin', 'assistant']}
      />
      {/* Admin-to-Coach thread route */}
      <GuardedRoute
        exact
        path="/admin/coach-messages/:id"
        component={AdminCoachThreadPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      {/* Admin oversight thread routes */}
      <GuardedRoute
        exact
        path="/admin/oversight/coach/:id"
        component={AdminOversightThreadPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path="/admin/oversight/assistant/:id"
        component={AdminOversightThreadPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminOversightCoach.link}
        component={AdminOversightCoachInboxPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminOversightAssistant.link}
        component={AdminOversightAssistantInboxPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      {/* Coach viewing admin thread */}
      <GuardedRoute
        exact
        path="/messages/admin/:id"
        component={CoachAdminThreadPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'assistant']}
      />
      {/* Coach messaging routes */}
      <GuardedRoute
        exact
        path="/messages/:conversationId"
        component={CoachThreadPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin', 'assistant']}
      />
      <GuardedRoute
        exact
        path={messages.link}
        component={CoachInboxPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin', 'assistant']}
      />
      <GuardedRoute
        exact
        path={adminDashboard.link}
        component={AdminDashboard}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminChecklistTemplate.link}
        component={AdminChecklistTemplate}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminChecklistProgress.link}
        component={AdminChecklistProgress}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminAssistants.link}
        component={AdminAssistants}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={assistantProfile.link}
        component={AdminAssistantProfile}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminOpsDashboard.link}
        component={AdminOpsDashboard}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminTimeEntries.link}
        component={AdminTimeEntries}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminDocuments.link}
        component={AdminDocuments}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin', 'assistant']}
      />
      <GuardedRoute
        exact
        path={adminPipeline.link}
        component={AdminPipeline}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminPageContent.link}
        component={AdminPageContent}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminPendingCoaches.link}
        component={AdminPendingCoaches}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={adminErrorLogs.link}
        component={AdminErrorLogs}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={assistantDashboard.link}
        component={AssistantDashboard}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['assistant']}
      />
      <GuardedRoute
        exact
        path={assistantTimeTracking.link}
        component={AssistantTimeTracking}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['assistant']}
      />
      <GuardedRoute
        exact
        path={adminActivity.link}
        component={AdminActivity}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={coachEngagement.link}
        component={CoachEngagementList}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={coachInsights.link}
        component={CoachInsights}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['admin']}
      />
      <GuardedRoute
        exact
        path={finishProfile.link}
        component={FinishProfile}
        auth={isAuthorized}
        isProfileFinished
      />
      <GuardedRoute
        exact
        path={editPlayer.link}
        component={EditPlayer}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
      />
      <GuardedRoute
        exact
        path={recommendations.link}
        component={RecommendationsPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin']}
      />
      <GuardedRoute
        exact
        path={compare.link}
        component={ComparePage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin']}
      />
      <GuardedRoute
        exact
        path={favorites.link}
        component={Players}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin']}
      />
      <GuardedRoute
        exact
        path={players.link}
        component={Players}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin']}
      />
      <GuardedRoute
        exact
        path={createPlayer.link}
        component={CreatePlayer}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin']}
      />
      <GuardedRoute
        exact
        path={`${players.link}/:id`}
        component={PlayerPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['coach', 'admin']}
      />
      {/* Assistant-only: list of own players */}
      <GuardedRoute
        exact
        path={assistantMyPlayers.link}
        component={AssistantMyPlayers}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['assistant']}
      />
      {/* Assistant-only: individual player profile (checklist view) */}
      <GuardedRoute
        exact
        path={assistantPlayerProfile.link}
        component={PlayerPage}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
        userRole={userRole}
        restrictToRoles={['assistant']}
      />
      <Route exact path={home.link} component={Home} />
      <Route
        exact
        path={createAccount.link}
        component={CreateAccount}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
      />
      <Route
        exact
        path={aboutUs.link}
        component={AboutUs}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
      />
      <Route
        exact
        path={summerTrips.link}
        component={SummerTrips}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
      />
      <Route
        exact
        path={faq.link}
        component={Faq}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
      />
      <Route
        exact
        path={sampleProfile.link}
        component={SampleProfile}
        auth={isAuthorized}
        isProfileFinished={isProfileFinished}
      />
      <Route exact path={resetPassword.link} component={ResetPassword} />
      <Route exact path={login.link}>
        <Login setIsAuthorized={setIsAuthorized} />
      </Route>
      <Route component={NotFound} />
    </Switch>
  );

  return (
    <div className="App">
      <Router>
        <Menu
          userName={user && user.firstName}
          userLastName={user && user.lastName}
          setIsAuthorized={setIsAuthorized}
          isAuthorized={isAuthorized}
          isProfileFinished={isProfileFinished}
          userRole={userRole}
        />
        {hasSidebar ? (
          <>
            <div className="admin-layout">
              {isAdmin ? (
                <AdminSidebar
                  userName={user && user.firstName}
                  setIsAuthorized={setIsAuthorized}
                />
              ) : (
                <CoachBar
                  userName={user && user.firstName}
                  userLastName={user && user.lastName}
                  setIsAuthorized={setIsAuthorized}
                  userRole={userRole}
                />
              )}
              <div className="admin-layout__content">
                {showToaster}
                {routeSwitch}
              </div>
            </div>
            <Footer />
          </>
        ) : (
          <div className="container">
            {showToaster}
            {routeSwitch}
          </div>
        )}
        {!hasSidebar && <Footer />}
        {isAuthorized && isProfileFinished && <CompareBar />}
        <CookieConsent />
        {showWhatsNew && (
          <WhatsNew user={user} onDismiss={() => setShowWhatsNew(false)} />
        )}
      </Router>
    </div>
  );
};

export default App;
